import { SidePanel, Input, Dropdown, Button, Icon } from '../ui';

interface Agency {
  id: string;
  name: string;
}

interface UserInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteUser: () => Promise<void>;
  inviteEmail: string;
  setInviteEmail: (email: string) => void;
  inviteRole: string;
  setInviteRole: (role: string) => void;
  inviteAgencyId: string;
  setInviteAgencyId: (agencyId: string) => void;
  inviteStatus: {
    loading: boolean;
    success: boolean;
    error?: string;
    inviteLink?: string;
  };
  agencies: Agency[];
}

const ROLE_OPTIONS = [
  { value: 'authenticated', label: 'User' },
  { value: 'developer', label: 'Developer' },
  { value: 'client', label: 'Client' },
  { value: 'admin', label: 'Admin' },
  { value: 'agency_owner', label: 'Agency Owner' },
];

export default function UserInviteModal({
  isOpen,
  onClose,
  onInviteUser,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  inviteAgencyId,
  setInviteAgencyId,
  inviteStatus,
  agencies,
}: UserInviteModalProps) {
  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      eyebrow="Team"
      title="Invite User"
      width={400}
      footer={
        inviteStatus.success ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button
              icon="send"
              disabled={inviteStatus.loading || !inviteEmail.trim() || !inviteAgencyId}
              onClick={onInviteUser}
            >
              {inviteStatus.loading ? 'Sending...' : 'Send Invite'}
            </Button>
            <Button variant="ghost" disabled={inviteStatus.loading} onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {inviteStatus.success ? (
        <div className="ck-stagger flex flex-col items-center text-center">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '52px',
              height: '52px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--status-up-soft)',
              color: 'var(--status-up)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <Icon name="check" size={24} />
          </span>

          <h3
            style={{
              margin: '0 0 var(--space-2)',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            Invite Sent
          </h3>
          <p style={{ margin: '0 0 var(--space-5)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            An invitation has been sent to{' '}
            <span className="t-num" style={{ color: 'var(--text-body)' }}>
              {inviteEmail}
            </span>
          </p>

          {inviteStatus.inviteLink && (
            <div style={{ width: '100%' }}>
              <div className="t-eyebrow" style={{ marginBottom: '6px', textAlign: 'left' }}>
                Invite Link
              </div>
              <div
                className="t-num"
                style={{
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 12px',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  wordBreak: 'break-all',
                  textAlign: 'left',
                }}
              >
                {inviteStatus.inviteLink}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Email"
            type="email"
            icon="mail"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="name@bullfinch.io"
          />

          <Dropdown
            label="Role"
            options={ROLE_OPTIONS}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            placeholder="Select role..."
          />

          <Dropdown
            label="Agency"
            options={agencies.map((agency) => ({ value: agency.id, label: agency.name }))}
            value={inviteAgencyId}
            onChange={(e) => setInviteAgencyId(e.target.value)}
            placeholder="Select agency..."
          />

          {inviteStatus.error && (
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--danger-500)' }}>
              {inviteStatus.error}
            </p>
          )}
        </div>
      )}
    </SidePanel>
  );
}
